import { getColorLabel, getSizeLabel, uniqueByLabel } from "./shopUtils";
import { productName } from "./productService";

/** Parse API stock quantity; null when the field is absent / non-numeric. */
export function parseStockQuantity(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.floor(n));
}

function normalizeStockLabel(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function asOptionArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item != null);
  if (value && typeof value === "object") return [value];
  if (typeof value === "string" && value.trim()) return [value];
  return [];
}

function cleanOptionLabel(value) {
  if (typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed || trimmed === "[object Object]") return "";
  return trimmed;
}

function formatProductSizeWithInches(size) {
  const label = getSizeLabel(size) || cleanOptionLabel(typeof size === "string" ? size : "");
  const inches = cleanOptionLabel(String(size?.inches ?? ""));
  if (!inches) return label;
  const lower = label.toLowerCase();
  if (lower.includes("inch") || lower.includes(inches.toLowerCase())) {
    return label;
  }
  return label ? `${label} (${inches}")` : `${inches}"`;
}

function productSizeRowColorLabel(row) {
  if (!row) return "";
  const fromColor = getColorLabel(row.color);
  if (fromColor) return fromColor;
  if (typeof row.color === "string") return cleanOptionLabel(row.color);
  return "";
}

export function productNameIncludesSticker(name) {
  return productName({ name }).toLowerCase().includes("sticker");
}

/** Stickers skip inventory checks (API quantity is ignored) — Giftshop parity. */
export function shouldSkipStockCheck(product) {
  return productNameIncludesSticker(product?.name);
}

/**
 * Raw product_sizes rows matching optional size / color labels.
 * Color match is skipped when the row has no color (e.g. stickers, cap).
 */
export function findProductSizeRows(product, options = {}) {
  const rows = asOptionArray(product?.product_sizes);
  if (!rows.length) return [];

  const sizeLabel = normalizeStockLabel(options.sizeLabel || "");
  const colorLabel = normalizeStockLabel(options.colorLabel || "");

  return rows.filter((row) => {
    const rowSize =
      formatProductSizeWithInches(row) || getSizeLabel(row);
    if (sizeLabel) {
      const rowSizeNorm = normalizeStockLabel(rowSize);
      const rowSizeOnly = normalizeStockLabel(getSizeLabel(row));
      if (rowSizeNorm !== sizeLabel && rowSizeOnly !== sizeLabel) {
        return false;
      }
    }

    const rowColor = normalizeStockLabel(productSizeRowColorLabel(row));
    if (!colorLabel) return true;
    if (!rowColor) return true;
    return rowColor === colorLabel;
  });
}

/**
 * Stock for a specific size + color selection from `product_sizes[].quantity`.
 * Falls back to product-level `quantity` only when there are no size rows.
 */
export function getVariantStockQuantity(product, options = {}) {
  const sizeLabel =
    options.sizeLabel ||
    (options.size
      ? formatProductSizeWithInches(options.size) || getSizeLabel(options.size)
      : "");
  const colorLabel =
    options.colorLabel ||
    (options.color ? getColorLabel(options.color) : "");

  const rows = asOptionArray(product?.product_sizes);
  if (rows.length) {
    const matched = findProductSizeRows(product, { sizeLabel, colorLabel });
    if (!matched.length) {
      if (sizeLabel || colorLabel) return 0;
      const totals = rows
        .map((row) => parseStockQuantity(row?.quantity))
        .filter((q) => q != null);
      if (!totals.length) return null;
      return totals.reduce((sum, q) => sum + q, 0);
    }

    const quantities = matched
      .map((row) => parseStockQuantity(row?.quantity))
      .filter((q) => q != null);
    if (!quantities.length) return null;
    return quantities.reduce((sum, q) => sum + q, 0);
  }

  return parseStockQuantity(product?.quantity);
}

/** Max purchasable units for the given selection (0 when sold out). */
export function getAvailableStock(product, options = {}) {
  if (shouldSkipStockCheck(product)) return Number.MAX_SAFE_INTEGER;
  return getVariantStockQuantity(product, options) ?? 0;
}

export function isVariantInStock(product, options = {}) {
  if (shouldSkipStockCheck(product)) return true;
  return getAvailableStock(product, options) > 0;
}

/**
 * Product-level stock for cards.
 * No color filter → sum across all size/color rows.
 */
export function getProductStockQuantity(product, options = {}) {
  if (shouldSkipStockCheck(product)) return Number.MAX_SAFE_INTEGER;

  const colorLabel =
    options.colorLabel ||
    (options.color ? getColorLabel(options.color) : "");
  const rows = asOptionArray(product?.product_sizes);

  if (rows.length) {
    const matched = colorLabel
      ? findProductSizeRows(product, { colorLabel })
      : rows;
    const quantities = matched
      .map((row) => parseStockQuantity(row?.quantity))
      .filter((q) => q != null);
    if (!quantities.length) return null;
    return quantities.reduce((sum, q) => sum + q, 0);
  }

  return parseStockQuantity(product?.quantity);
}

function isToteProduct(product) {
  const name = productName(product).toLowerCase();
  const sku = String(product?.sku_code || "")
    .trim()
    .toLowerCase();
  return name.includes("tote") || sku === "tb" || sku.startsWith("tb");
}

function isHoodieProduct(product) {
  const name = productName(product).toLowerCase();
  const sku = String(product?.sku_code || "")
    .trim()
    .toLowerCase();
  return (
    name.includes("hoodie") ||
    name.includes("sweatshirt") ||
    sku === "hd" ||
    sku.startsWith("hd")
  );
}

/** Tote → beige; hoodie → ash if available; apparel → white → black. */
function defaultPreferColorLabels(product) {
  if (isToteProduct(product)) {
    return ["beige", "cream", "natural", "ivory"];
  }
  if (isHoodieProduct(product)) {
    return ["ash", "white", "black", "beige"];
  }
  return ["white", "black", "beige"];
}

function colorMatchesPreferLabel(colorLabel, preferLabel) {
  const left = normalizeStockLabel(colorLabel);
  const right = normalizeStockLabel(preferLabel);
  if (!left || !right) return false;
  if (left === right) return true;
  // Tote catalog often labels beige as cream/natural.
  const beigeFamily = new Set(["beige", "cream", "natural", "ivory"]);
  return beigeFamily.has(left) && beigeFamily.has(right);
}

/**
 * First in-stock size+color pair.
 * Tote → beige; hoodie → ash if available; else white → black (or options.preferColorLabels).
 * Returns null only when every color/size combination is sold out.
 */
export function findAvailableVariant(product, options = {}) {
  if (shouldSkipStockCheck(product)) {
    return {
      size: asOptionArray(product?.product_sizes)[0] || null,
      color: asOptionArray(product?.color)[0] || null,
    };
  }

  const sizes = uniqueByLabel(
    asOptionArray(product?.product_sizes),
    getSizeLabel
  );
  const colors = uniqueByLabel(asOptionArray(product?.color), getColorLabel);
  const preferLabels = (
    options.preferColorLabels || defaultPreferColorLabels(product)
  ).map((label) => normalizeStockLabel(label));

  const orderedColors = [];
  const seenColor = new Set();
  preferLabels.forEach((label) => {
    const match = colors.find((color) =>
      colorMatchesPreferLabel(getColorLabel(color), label)
    );
    if (!match) return;
    const key = normalizeStockLabel(getColorLabel(match));
    if (seenColor.has(key)) return;
    seenColor.add(key);
    orderedColors.push(match);
  });
  colors.forEach((color) => {
    const key = normalizeStockLabel(getColorLabel(color));
    if (!key || seenColor.has(key)) return;
    seenColor.add(key);
    orderedColors.push(color);
  });

  if (!sizes.length && !orderedColors.length) {
    return (getProductStockQuantity(product) ?? 0) > 0
      ? { size: null, color: null }
      : null;
  }

  if (!sizes.length) {
    for (const color of orderedColors) {
      if (isVariantInStock(product, { colorLabel: getColorLabel(color) })) {
        return { size: null, color };
      }
    }
    return null;
  }

  if (!orderedColors.length) {
    for (const size of sizes) {
      if (isVariantInStock(product, { size })) {
        return { size, color: null };
      }
    }
    return null;
  }

  for (const color of orderedColors) {
    for (const size of sizes) {
      if (isVariantInStock(product, { size, color })) {
        return { size, color };
      }
    }
  }

  // Fallback: any size row with quantity, even if color list is incomplete.
  const rows = asOptionArray(product?.product_sizes);
  const stockedRow = rows.find((row) => (parseStockQuantity(row?.quantity) ?? 0) > 0);
  if (stockedRow) {
    const rowColorLabel = productSizeRowColorLabel(stockedRow);
    const color =
      orderedColors.find(
        (item) =>
          normalizeStockLabel(getColorLabel(item)) ===
          normalizeStockLabel(rowColorLabel)
      ) || null;
    return { size: stockedRow, color };
  }

  return null;
}

/** True when any size/color variant has stock (card-level sold out). */
export function isProductInStock(product, options = {}) {
  if (shouldSkipStockCheck(product)) return true;
  if (options.colorLabel || options.color || options.size || options.sizeLabel) {
    return (getProductStockQuantity(product, options) ?? 0) > 0;
  }
  return findAvailableVariant(product) != null;
}

export function isSizeAvailableForColor(product, size, color) {
  return isVariantInStock(product, {
    size,
    colorLabel: color ? getColorLabel(color) : "",
  });
}

export function isColorAvailableForSize(product, color, size) {
  return isVariantInStock(product, {
    size,
    colorLabel: getColorLabel(color),
  });
}

/** Units of this product/variant already in the session cart. */
export function cartQuantityForVariant(cartItems, product, options = {}) {
  const productId = String(product?.id || product?.productId || "");
  if (!productId) return 0;
  const sizeLabel = normalizeStockLabel(
    options.sizeLabel || getSizeLabel(options.size) || ""
  );
  const colorLabel = normalizeStockLabel(
    options.colorLabel || getColorLabel(options.color) || ""
  );

  return (cartItems || []).reduce((sum, item) => {
    if (String(item?.productId || item?.id || "") !== productId) return sum;
    if (item?.isPack || String(item?.type || "").toLowerCase() === "pack") {
      return sum;
    }
    const itemSize = normalizeStockLabel(item?.size || item?.dimensions || "");
    const itemColor = normalizeStockLabel(item?.color || item?.variety || "");
    if (sizeLabel && itemSize && itemSize !== sizeLabel) return sum;
    if (colorLabel && itemColor && itemColor !== colorLabel) return sum;
    return sum + (Number(item.quantity) || 0);
  }, 0);
}

/** Remaining stock after accounting for items already in the cart. */
export function getRemainingStock(product, options = {}, cartItems = []) {
  if (shouldSkipStockCheck(product)) return Number.MAX_SAFE_INTEGER;
  const available = getAvailableStock(product, options);
  const inCart = cartQuantityForVariant(cartItems, product, options);
  return Math.max(0, available - inCart);
}
